import { alpha, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { DashboardCardValue } from "web_myplann/Dashboard/Components/DashboardCardValue";
import { EvaluationDelta } from "web_myplann/Dashboard/Components/EvaluationDelta";
import { useCurrencySign } from "web_myplann/Dashboard/Hooks/useCurrencySign";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import {
	DASHBOARD_CARD_EVALUATION_HEIGHT,
	DASHBOARD_CARD_TOOLTIP_OFFSET,
	DASHBOARD_CARD_VALUE_BLOCK_HEIGHT,
} from "web_myplann/Dashboard/Utils/dashboardLayout";
import { getDashboardValueState } from "web_myplann/Dashboard/Utils/getDashboardValueState";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyWidgetTooltip } from "web_myplann/Shared/Components/PropertyWidgetTooltip";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type Props = {
	dashboardData: DashboardType;
	navigateToDashboardDetail?: () => void;
	onTooltipToggle?: (open: boolean) => void;
};

export const DashboardCardV2Content: FC<Props> = ({
	dashboardData,
	navigateToDashboardDetail,
	onTooltipToggle,
}) => {
	const { t } = useResource();
	const { palette, spacingTokens, iconSize } = useTheme();
	const currencySign = useCurrencySign(dashboardData.currency);
	const white = palette.darkmodeText.white;

	const { isMissingValueWithItems, evaluation } =
		getDashboardValueState(dashboardData);
	const { netValue, overviewType } = dashboardData;

	const showNetValue =
		netValue !== undefined &&
		overviewType === PortfolioReportingOverviewTypeEnum.Total;

	return (
		<Stack width="100%" height="100%" alignItems="center" pt={spacingTokens.sm}>
			<Stack
				alignItems="center"
				justifyContent="center"
				height={DASHBOARD_CARD_VALUE_BLOCK_HEIGHT}
			>
				<Stack direction="row" alignItems="center" mb={spacingTokens.xs}>
					<Typography variant="bodyMedium" color={alpha(white, 0.8)}>
						{dashboardData.type}
					</Typography>
					<PropertyWidgetTooltip
						title={t(Resources.Dashboard.TitleCard.tooltip)}
						onToggle={onTooltipToggle}
						offset={DASHBOARD_CARD_TOOLTIP_OFFSET}
					/>
				</Stack>
				<DashboardCardValue
					dashboardData={dashboardData}
					isMissingValueWithItems={isMissingValueWithItems}
				/>
				<Stack
					height={DASHBOARD_CARD_EVALUATION_HEIGHT}
					justifyContent="center"
					mt={spacingTokens.xs}
				>
					{evaluation !== undefined && (
						<EvaluationDelta evaluation={evaluation} />
					)}
				</Stack>
				{showNetValue && (
					<Stack direction="row" alignItems="center" mt={spacingTokens.lg}>
						<Typography variant="body1" color={white} tabular>
							{moneyFormatter(
								Math.round(netValue),
								currencySign,
								`0 ${currencySign}`,
							)}
						</Typography>
						<PropertyWidgetTooltip
							title={t(Resources.Dashboard.TitleCard.netValueTooltip)}
							onToggle={onTooltipToggle}
							size={iconSize.xs}
							offset={DASHBOARD_CARD_TOOLTIP_OFFSET}
						/>
					</Stack>
				)}
			</Stack>

			{navigateToDashboardDetail && (
				<Box mt={spacingTokens.sm}>
					<Button
						variant="onGradient"
						sx={{ color: dashboardData.gradientFrom }}
						onClick={navigateToDashboardDetail}
					>
						{t(Resources.Dashboard.TitleCard.title)}
					</Button>
				</Box>
			)}
		</Stack>
	);
};
